<template>
  <div id="appeal" :class="['content', programStyle]">
    <div class="title-container">
      <div class="title">{{ $t('appeals-form') }}</div>
    </div>
    <div v-if="displayResults" class="review-container">
      <div class="result-container gray">{{ $t('completed') }}</div>
      <div class="reason">{{ $t('reason', [mostRecentStatus.reason]) }}</div>
    </div>
    <form class="form-container" @submit.prevent="checkValidation">
      <div class="item-container" v-for="appeal in appeals" :key="appeal.dialId">
        <div class="dial-info-container">
          <div class="left-container flex-column">
            <div class="smmt flex-column" @click="selectDial(appeal)" v-if="appeal.dialProgress || appeal.progress">
              <IconProgress
                :progress="appeal.dialProgress || appeal.progress"
                :targets="appeal.dialTargets"
                :color="appeal.dialColor"
              />
              <div class="smmtInner" v-if="appeal.dialProgress">
                {{ appeal.dialProgress[0] }}
              </div>
              <div class="smmtInner" v-if="appeal.progress">
                {{ appeal.progress }}
              </div>
            </div>
            <div
              @click="selectDial(appeal)"
              v-if="!appeal.dialProgress && !appeal.progress"
              :class="['circle', appeal.dialColor]"
            >
              <div class="inner" v-if="appeal.dialResultText">
                {{ appeal.dialResultText }}
              </div>
            </div>
            <div v-if="displayResults" class="result">
              <div
                v-if="
                  appeal.status &&
                  (appeal.status.result === 'Approved' ||
                    appeal.status.result === 'Approve' ||
                    appeal.status.result === 'Declined')
                "
              >
                {{ appeal.status.result }}
              </div>
              <div
                v-if="
                  appeal.status &&
                  appeal.status.result !== 'Approved' &&
                  appeal.status.result !== 'Approve' &&
                  appeal.status.result !== 'Declined'
                "
              >
                Approve to {{ appeal.status.result }}
              </div>
              <div v-if="!appeal.status">{{ appeal.dialResult }}</div>
            </div>
          </div>
          <div class="right-container flex-column">
            <div class="subtitle">{{ appeal.dialTitle }}</div>
            <div class="supporting-docs" v-if="appeal.uploads || !appeal.notAppealed">
              {{ $t('supporting-documents') }}
            </div>
            <Upload
              v-if="!appeal.uploads && !appeal.notAppealed"
              :appeal="appeal"
              @sendurl="getUploadForNewForm"
              @removeUpload="removeUpload"
            />
            <div v-if="appeal.uploads">
              <div class="support-docs" v-for="upload in appeal.uploads" :key="upload.key">
                <span v-if="upload.label === appeal.dialTitle">{{ upload.originalFilename }}</span>
                <a v-if="upload.label === appeal.dialTitle" class="relative" target="_blank" :href="upload.url">
                  <img class="download-icon" src="@/assets/img/download-arrow.png" />
                </a>
                <Remove class="delete-file" @click="openDeleteModal(upload)" />
              </div>
            </div>
            <span v-if="appeal.uploads && !appeal.notAppealed">{{ $t('additional-documents') }}</span>
            <Upload
              v-if="appeal.uploads && !appeal.notAppealed && appealType !== 'view'"
              :appeal="appeal"
              @sendurl="getUploadForExistingForm"
              @removeUpload="removeAdditionalUpload"
            />
            <button v-if="additionalUploads.length > 0" class="save-additional" @click.prevent="saveAdditionalDocs">
              {{ $t('save-additional-docs-and-exit') }}
            </button>
          </div>
        </div>
        <SMMTAppeal
          :dialInfo="appeal.dialDataArr[0]"
          :disabled="appealType === 'view'"
          :displayResults="displayResults"
        />
        <div class="helper-text-container">
          <span class="helper-text"
            >Please provide reasons for each decision you make, outlining specific requirements, "see documents" is not
            a valid reason.</span
          >
          <span class="helper-text red"
            >Details available to appeal are highlighted in <span class="bold">red</span>.</span
          >
        </div>
      </div>
      <div class="bottom-container flex-column">
        <p class="section-title">Sign Document</p>
        <span class="signature-info">{{ $t('acknowledge-warning') }}</span>
        <div class="bottom-info">
          <div class="flex-column">
            <span>Name:</span>
            <input readonly v-if="!otherAppealInfo" v-model="signature" />
            <input readonly v-if="otherAppealInfo" v-model="otherAppealInfo.dealerSignature" />
          </div>
          <div class="flex-column">
            <span>Date:</span>
            <div v-if="!otherAppealInfo" class="date">{{ date | date }}</div>
            <div v-if="otherAppealInfo" class="date">{{ otherAppealInfo.formCreatedAt | date }}</div>
          </div>
        </div>
        <div class="button-container">
          <button class="exit-btn" @click="exit" type="button">{{ $t('exit') }}</button>
          <button
            v-if="appealType !== 'view'"
            value="save"
            :class="[reasonValidationCheck ? '' : 'disabled', 'small-btn']"
            :disabled="!reasonValidationCheck && !loader"
          >
            {{ $t('submit') }}
          </button>
        </div>
      </div>
    </form>
    <Modal class="modal-screens" v-show="isModalOpen">
      <template v-slot:body class="body">
        <span
          >You are submitting <b>{{ appealedItemsCount }}</b> items to be appealed. Your dealership is limited to one
          appeal submission for this time period. Please confirm your entries are correct and select Submit or click
          Cancel.</span
        >
        <div class="modal-button-container">
          <button class="submit" @click="save">{{ $t('submit') }}</button>
          <button @click="isModalOpen = false">{{ $t('cancel') }}</button>
        </div>
      </template>
    </Modal>
    <Modal class="modal-screens" v-show="isDeleteUploadModalOpen">
      <template v-slot:body class="body">
        <span>Are you sure you want to delete this file?</span>
        <div class="modal-button-container">
          <button class="submit" @click="deleteUpload">{{ $t('submit') }}</button>
          <button @click="closeDeleteModal">{{ $t('cancel') }}</button>
        </div>
      </template>
    </Modal>
    <Modal class="modal-screens" v-show="isDialModalOpen">
      <template v-slot:body class="body" v-if="dialInfo">
        <div class="close" @click="isDialModalOpen = false"><Close /></div>
        <DialInfo :dialInfo="dialInfo" :period="period" />
      </template>
    </Modal>
  </div>
</template>

<script>
import 'vue-multiselect/dist/vue-multiselect.min.css';
import Upload from '@/components/Upload.vue';
import Remove from '@/assets/icons/delete-icn.svg';
import Modal from '@/components/Modal.vue';
import IconProgress from '@/components/IconProgress.vue';
import DialInfo from '@/components/DialInfo.vue';
import Close from '@/assets/icons/x_icon_round.svg';
import SMMTAppeal from '@/components/Dials/SMMTAppeal.vue';
import { mapState } from 'vuex';

export default {
  name: 'CCAppealForm',
  components: { Upload, Modal, IconProgress, DialInfo, Close, Remove, SMMTAppeal },
  props: {
    appealType: {
      type: String,
      required: true,
    },
    config: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      value: 'appeals',
      url: null,
      appeals: null,
      uploads: [],
      additionalUploads: [],
      date: new Date(),
      status: null,
      isModalOpen: false,
      isDialModalOpen: false,
      submissionSuccess: false,
      loader: null,
      dialInfo: null,
      comments: null,
      isDeleteUploadModalOpen: false,
      fileToDelete: null,
      appealInfo: null,
      appealedItemsCount: 0,
    };
  },
  methods: {
    save() {
      if (this.reasonValidationCheck) {
        // this.loader = this.$loading.show();
        const { entity, domain, signature } = this;
        const items = [];
        const allDialAppealsArr = [];

        this.appeals.forEach((appeal) => {
          items.push(appeal);
          appeal.dialDataArr[0].appealInfo[0].data.forEach((row, index) => {
            if (row.appealable) {
              allDialAppealsArr.push({
                dialId: appeal.dialId,
                appealResult: row.selection.value,
                appealReason: appeal.dialDataArr[0].appealInfo[1].data[index].data,
                ordinal: row.ordinal,
              });
            }
          });
        });

        const params = {
          domain,
          entity,
          items,
          allDialAppealsArr,
          uploads: this.uploads,
          status: this.status,
          dealerSignature: signature,
          result: 'Submitted',
          layout: 'Appeal',
          type: 'Appeal',
          configId: this.config.id,
        };
        this.submitForm(params);
      }
    },
    checkValidation() {
      if (this.reasonValidationCheck) {
        this.calculateAppealedItemsCount();
        this.isModalOpen = true;
      }
    },
    commentsValidation() {
      if (this.comments) {
        return true;
      }
      return false;
    },
    submitForm(params) {
      this.$store
        .dispatch('submitForm', params)
        .then(() => {
          this.isModalOpen = false;
          this.submissionSuccess = true;
          this.exit();
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        })
        .finally(() => {
          this.loader.hide();
        });
    },
    exit() {
      this.$router.push({
        name: 'Dealer-Forms',
      });
    },
    saveAdditionalDocs() {
      const { formId } = this;
      const loader = this.$loading.show();

      if (this.formId && this.additionalUploads.length > 0) {
        const params = {
          formId,
          uploads: this.additionalUploads,
        };

        this.$store.dispatch('uploadDocs', params).then(() => {
          loader.hide();
          this.exit();
        });
      }
    },
    getUploadForNewForm(res) {
      const upload = {
        originalFilename: res.res.data.fileInfo.originalFilename,
        adjustedFilename: res.res.data.fileInfo.adjustedFilename,
        label: res.appeal.dialTitle,
        dialId: res.appeal.dialId,
      };
      this.uploads.push(upload);
    },
    getUploadForExistingForm(res) {
      const upload = {
        originalFilename: res.res.data.fileInfo.originalFilename,
        adjustedFilename: res.res.data.fileInfo.adjustedFilename,
        label: res.appeal.dialTitle,
        dialId: res.appeal.dialId,
      };
      this.additionalUploads.push(upload);
    },
    removeUpload(item) {
      this.uploads.forEach((upload, index) => {
        if (upload.originalFilename === item.file) {
          this.uploads.splice(index, 1);
        }
      });
    },
    removeAdditionalUpload(item) {
      this.additionalUploads.forEach((upload, index) => {
        if (upload.originalFilename === item.file) {
          this.additionalUploads.splice(index, 1);
        }
      });
    },
    getAppeals() {
      this.appeals = JSON.parse(JSON.stringify(this.selectedForm));
      this.appeals.forEach((appeal) => {
        if (this.appealType === 'submit') {
          this.$set(appeal, 'notAppealed', false);
        }
        this.$set(appeal, 'checkBoxLabel', 'Not Appealing');
        if (
          appeal.notAppealed === '1' ||
          appeal.notAppealed === 'true' ||
          appeal.notAppealed === 'dealerNotAppealing' ||
          appeal.notAppealed === 'fieldNotAppealing'
        ) {
          appeal.notAppealed = true;
        } else {
          appeal.notAppealed = false;
        }
      });
    },
    confirmLeave() {
      if (!this.submissionSuccess && !this.otherAppealInfo) {
        return window.confirm(this.$t('unsaved-changes'));
      }
      return true;
    },
    beforeWindowUnload(e) {
      if (this.confirmStayInDirtyForm()) {
        // Cancel the event
        e.preventDefault();
        // Chrome requires returnValue to be set
        e.returnValue = '';
      }
    },
    confirmStayInDirtyForm() {
      return !this.confirmLeave();
    },
    selectDial(dial) {
      const { period, entity, domain } = this;
      const dialIdObj = dial.dialId;
      const dialId = dialIdObj;

      if (dial.dialColor === 'FI' || dial.dialColor === 'X' || dial.dialTypeId === 'SNM' || !dial.dialColor) {
        return;
      }
      const loader = this.$loading.show();
      this.$store.dispatch('getDial', { period, entity, dialId, domain }).then((res) => {
        this.isDialModalOpen = true;
        this.dialInfo = res[0];
        loader.hide();
      });
    },
    openDeleteModal(selectedFile) {
      this.isDeleteUploadModalOpen = true;
      this.fileToDelete = selectedFile;
    },
    closeDeleteModal() {
      this.isDeleteUploadModalOpen = false;
      this.fileToDelete = null;
    },
    deleteUpload() {
      const loader = this.$loading.show();

      const { formId, fullKey } = this.fileToDelete;

      this.$store
        .dispatch('deleteUpload', { formId, fullKey })
        .then((res) => {
          if (res) {
            this.closeDeleteModal();
            this.$notify({
              group: 'primary',
              type: 'success',
              text: 'File successfully deleted!',
            });
            this.exit();
          }
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        })
        .finally(() => {
          loader.hide();
        });
    },
    calculateAppealedItemsCount() {
      let count = 0;
      this.appeals.forEach((appeal) => {
        appeal.dialDataArr[0].appealInfo[0].data.forEach((row) => {
          if (row.selection.value === 'Y') {
            count += 1;
          }
        });
      });
      this.appealedItemsCount = count;
      count = 0;
    },
  },
  computed: {
    ...mapState('global', ['programStyle', 'programName']),
    ...mapState(['otherAppealInfo', 'selectedForm']),
    entity() {
      return this.$route.params.entity;
    },
    domain() {
      return this.$route.params.domain;
    },
    period() {
      if (this.otherAppealInfo) {
        return this.otherAppealInfo.config.period;
      }
      if (this.config) {
        return this.config.period;
      }
      return null;
    },
    mostRecentStatus() {
      if (this.otherAppealInfo) {
        return this.otherAppealInfo.statuses[0];
      }
      return null;
    },
    signature() {
      const user = JSON.parse(localStorage.getItem('stellantis-user'));
      const fullName = `${user.firstName} ${user.lastName}`;

      return fullName;
    },
    reasonValidationCheck() {
      const valid = true;
      // this.appeals.forEach((appeal) => {
      //   if ((appeal.reason || appeal.reasonForAppeal || appeal.notAppealed) && valid) {
      //     valid = true;
      //   } else {
      //     valid = false;
      //   }
      // });
      return valid;
    },
    formId() {
      if (this.otherAppealInfo) {
        return this.otherAppealInfo.formId;
      }
      return null;
    },
    displayResults() {
      if (this.otherAppealInfo && this.otherAppealInfo.displayResults === 'Y') {
        return true;
      }
      return false;
    },
  },
  watch: {},
  created() {
    this.getAppeals();
    window.addEventListener('beforeUnload', this.beforeWindowUnload);
  },
  beforeDestroy() {
    this.$emit('exit');
    window.removeEventListener('beforeunload', this.beforeWindowUnload);
  },
  beforeRouteLeave(to, from, next) {
    // If the form is dirty and the user did not confirm leave,
    // prevent losing unsaved changes by canceling navigation
    if (this.confirmStayInDirtyForm()) {
      next(false);
    } else {
      // Navigate to next view
      next();
    }
  },
};
</script>

<style lang="scss" scoped>
.content {
  color: #000000;
  text-align: left;
  .link {
    color: #243882;
  }
}
.title-container {
  margin-left: 30px;
  margin-top: 30px;
}
.title {
  margin-bottom: 24px;
  color: #243882;
  font-size: 21px;
  font-weight: bold;
  text-transform: uppercase;
}
.item-container {
  display: flex;
  flex-direction: column;
  margin: 0 30px 30px 0px;
  border-bottom: 1px solid $border;
  padding-bottom: 30px;

  .dial-info-container {
    display: flex;
    margin-bottom: 20px;
    margin-left: 30px;
    .right-container {
      width: 100%;
      margin-left: 10px;
      font-size: 14px;
      input {
        height: 100px;
        width: initial;
        background: $input-background;
        border: none;
        margin-bottom: 20px;
      }
      .support-docs {
        margin-bottom: 10px;
        display: flex;
        align-items: center;
      }
    }
    .left-container {
      width: 80px;
      align-items: center;
    }
  }
  .helper-text-container {
    display: flex;
    flex-direction: column;
    margin-top: 15px;
    margin-left: 30px;
    .helper-text {
      font-size: 14px;
      &.red {
        color: $error;
      }
      .bold {
        font-weight: bold;
      }
    }
  }
}

.subtitle {
  font-size: 18px;
  font-weight: bold;
  text-transform: uppercase;
  margin-bottom: 30px;
}
.description {
  margin-bottom: 10px;
}
.bottom-container {
  margin: 30px 30px 20px;
  .section-title {
    color: $primary;
    font-size: 18px;
    text-transform: uppercase;
    font-weight: bold;
  }
  .signature-info {
    margin-bottom: 20px;
  }
  .helper-text {
    font-size: 14px;
    .helper-text-requirements {
      margin-bottom: 0px;
      padding-left: 15px;
    }
  }
}
.bottom-info {
  display: flex;
  input {
    width: initial;
    background: $input-background;
    border: none;
    margin-bottom: 20px;
    margin-right: 30px;
    &:read-only {
      cursor: default;
    }
  }
}
.button-container {
  display: flex;
  margin-top: 40px;
  align-items: center;
  justify-content: center;
  button {
    margin-right: 30px;
  }
}
.date {
  width: 160px;
  background: $input-background;
  border: none;
  margin-bottom: 20px;
  margin-right: 20px;
  padding: 13px 0px 13px 20px;
}
.modal-container {
  .bottom-container {
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
  }
}
.modal-header {
  margin-bottom: 20px;
}
.modal-button-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  .submit {
    margin-right: 30px;
  }
}
.modal-body {
  display: flex;
  flex-direction: column;
  .close {
    display: flex;
    justify-content: flex-end;
    svg {
      height: 30px;
      width: 30px;
      cursor: pointer;
    }
  }
  span {
    text-align: center;
  }
}
.circle {
  margin-right: 0px;
}
.supporting-docs {
  margin-bottom: 10px;
  font-weight: bold;
}
.save-additional {
  width: 300px;
  margin-top: 10px;
}
.result {
  margin-top: 10px;
  text-align: center;
}
.smmt {
  align-items: center;
}
.checkbox-container {
  display: block;
  margin-bottom: 20px;
  ::v-deep {
    label {
      color: black;
    }
    input {
      z-index: 0 !important;
    }
  }
}
.delete-file {
  height: 15px;
  width: 15px;
  margin-left: 10px;
  cursor: pointer;
}
</style>
